// Paso 2 del asistente (PE-A3a y PE-A3a2): cálculo en vivo de la carga de fuego por sector y por grupo de sectores
// sin separación cortafuego. Solo usa el motor (pe-carga-fuego.ts); nada se calcula a mano en la pantalla.

import { agruparSectores, calcularCargaFuego, MaterialRD164, ResultadoCargaFuego, SectorRD164 } from './pe-carga-fuego';
import {
  FilaCargaEditable, FilaEvaluacion, guardarEvaluacion, Ocupacion, rDeFila, RSector, SectorEditable,
} from './pe-datos';
import type { Traza } from './pe-tipos';

export interface SectorEnPantalla {
  sector: SectorEditable;
  filas: FilaCargaEditable[];
  r: RSector;
}

export function filaAMaterial(f: FilaCargaEditable): MaterialRD164 {
  if (f.method !== 'materiales' || f.q_unit !== 'MJ/kg') {
    throw new Error(`"${f.material}": por ahora solo se calcula por materiales (qi en MJ/kg × kg)`);
  }
  return {
    nombre: f.material || '(sin nombre)',
    masaKg: f.mass_kg ?? 0,
    qiMJkg: f.q_value,
    qiOrigen: f.q_source === 'manual' ? 'manual' : 'tabla',
    qiFuente: f.q_source_note ?? undefined,
    ci: f.ci,
    ciOrigen: f.ci_origin,
    ciFuente: f.ci_source ?? undefined,
  };
}

export function sectorAMotor(s: SectorEnPantalla): SectorRD164 {
  return {
    nombre: s.sector.name.trim() || '(sin nombre)',
    areaM2: s.sector.area_m2,
    r: s.r.r,
    rOrigen: s.r.rOrigen,
    rFuente: s.r.rFuente ?? undefined,
    materiales: s.filas.map(filaAMaterial),
    separadoCortafuego: s.sector.fire_separated,
    grupo: s.sector.fire_separated ? undefined : (s.sector.group_key?.trim() || undefined),
  };
}

export type Calculo = { ok: true; resultado: ResultadoCargaFuego } | { ok: false; error: string };

export function calcularSector(s: SectorEnPantalla): Calculo {
  try {
    return { ok: true, resultado: calcularCargaFuego(sectorAMotor(s)) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export interface Agrupacion {
  nombre: string;
  miembros: string[];
  calculo: Calculo;
  trazas: Traza[];
}

// Grupos de sectores sin separación cortafuego (PE-28b): se evalúan juntos con el R mayor.
// Solo se listan los grupos de 2 o más sectores; un sector suelto se evalúa solo.
export function calcularGrupos(sectores: SectorEnPantalla[]): Agrupacion[] {
  const validos: SectorRD164[] = [];
  for (const s of sectores) {
    if (s.sector.fire_separated) continue;
    try { validos.push(sectorAMotor(s)); } catch { /* el error ya se muestra en el sector */ }
  }
  const { sectores: unidos, trazas } = agruparSectores(validos);
  return unidos
    .filter(u => u.separadoCortafuego === false && validos.filter(v => (v.grupo ?? '(sin separación cortafuego)') === u.grupo).length > 1)
    .map(u => {
      let calculo: Calculo;
      try { calculo = { ok: true, resultado: calcularCargaFuego(u) }; } catch (e) {
        calculo = { ok: false, error: e instanceof Error ? e.message : String(e) };
      }
      return {
        nombre: u.grupo ?? u.nombre,
        miembros: u.nombre.split(' + '),
        calculo,
        trazas: trazas.filter(t => t.nota?.includes(`"${u.grupo}"`)),
      };
    });
}

// Guarda la evaluación 'carga_fuego' de cada sector (entradas, resultado y trazas). Si el sector está en
// un grupo, el resultado del grupo va también en result.grupo. Es lo único que guarda totales.
export async function guardarCargaFuego(
  companyId: string, planId: string, sectores: (SectorEnPantalla & { sector: SectorEditable & { id: string } })[],
  usuarioId: string | null,
): Promise<FilaEvaluacion[]> {
  const grupos = calcularGrupos(sectores);
  const out: FilaEvaluacion[] = [];
  for (const s of sectores) {
    const c = calcularSector(s);
    if (!c.ok) continue;
    const g = grupos.find(x => x.miembros.includes(c.resultado.sector) && !s.sector.fire_separated);
    const { trazas, ...resultado } = c.resultado;
    out.push(await guardarEvaluacion({
      companyId, planId, sectorId: s.sector.id, method: 'carga_fuego', usuarioId,
      inputs: {
        areaM2: s.sector.area_m2, r: s.r.r, rOrigen: s.r.rOrigen, rFuente: s.r.rFuente,
        separadoCortafuego: s.sector.fire_separated, grupo: s.sector.group_key ?? null,
        materiales: s.filas.map(filaAMaterial),
      },
      result: {
        ...resultado,
        ...(g && g.calculo.ok ? { grupo: { nombre: g.nombre, miembros: g.miembros, qsMJm2: g.calculo.resultado.qsMJm2,
          qsKcalm2: g.calculo.resultado.qsKcalm2, nivelRD164: g.calculo.resultado.nivelRD164,
          clasificacionArt139: g.calculo.resultado.clasificacionArt139 } } : {}),
      },
      traces: trazas.concat(g && g.calculo.ok ? g.trazas.concat(g.calculo.resultado.trazas) : []),
    }));
  }
  return out;
}

// R del sector desde sus columnas (M34); sin R guardado, 1 por defecto.
export function rDeSector(s: Pick<SectorEditable, 'r_value' | 'r_origin' | 'r_source'>): RSector {
  return rDeFila(s);
}

export const ETIQUETA_OCUPACION: Record<Ocupacion, string> = {
  industrial: 'Industrial', almacenamiento: 'Almacenamiento', oficinas: 'Oficina', mercantil: 'Mercantil',
  residencial: 'Residencial', alojamiento: 'Alojamiento', educacion: 'Educación', salud: 'Salud',
  reunion_publica: 'Reunión pública', otra: 'Otra',
};

// Actividades del sector en el paso 2 (pedido de JC: almacenamiento / industrial / oficina / otra).
export const OCUPACIONES_SECTOR: Ocupacion[] = ['almacenamiento', 'industrial', 'oficinas', 'otra'];

export const ETIQUETA_ART139 = { leve: 'Leve', ordinario: 'Ordinario', extra: 'Extra' } as const;
